import { isApolloError, reportAPIError, reportApolloError, reportGraphQLError } from './errorUtils';
import { captureError } from './sentry';
import { GraphQLError } from 'graphql';
import { isHTTPAccessFallbackError } from 'next/dist/client/components/http-access-fallback/http-access-fallback';
import { isRedirectError } from 'next/dist/client/components/redirect-error';

/*
 * This function is used to report errors that occur on the server.
 * Do not use this function to report errors in client components.
 * Use `reportMykissClientError` instead.
 */
export function reportMykissServerError(
  error?: unknown,
  context?: string,
  severity?: 'info' | 'warning',
  otherData?: object,
): void {
  if (!error) {
    return;
  }

  // The below check of error.text is a replacement for `error instanceof Response`, as the Response
  // class is not available in the Node environment. A Fetch API response should have a `text`
  // function (we use it in `reportAPIError`), so we use it's existance to decide the error is from
  // a Fetch request.
  // @ts-ignore
  if (error.text) {
    reportAPIError(error as Response, severity, otherData, context);
  } else if (isApolloError(error)) {
    // Apollo 4: All Apollo error types (GraphQL errors, network errors, parsing errors)
    reportApolloError(error, severity, otherData, context);
  } else if (error instanceof GraphQLError) {
    reportGraphQLError(error, severity, otherData, context);
  } else if (isRedirectError(error)) {
    // Next throws an error when it encounters a redirect.
    // We must re-throw the EXACT error object for Next.js to handle it properly.
    throw error;
  } else if (isHTTPAccessFallbackError(error)) {
    // Next throws an error when it encounters a Not found.
    // We must re-throw the EXACT error object for Next.js to handle it properly.
    throw error;
  } else if (error instanceof Error) {
    captureError(error, {
      context,
      severity: error.name === 'NotFoundError' ? (severity ?? 'info') : (severity ?? 'error'),
      metadata: otherData as Record<string, unknown>,
    });
  } else {
    // For non-Error values, convert to Error
    const errorMessage = typeof error === 'string' ? error : JSON.stringify(error);
    captureError(new Error(errorMessage), {
      context,
      severity: severity ?? 'error',
      metadata: { originalError: error, ...(otherData as Record<string, unknown>) },
    });
  }
}
